// hooks/useCreateLeague.js
import { useMutation, useQueryClient } from "react-query";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";
import { useToast } from "../useToast";
import { useDispatch } from "react-redux";
import { setLeagueId } from "../../features/league/leagueReducer";

export const useCreateLeague = (navigate) => {
  // Passing navigate function as a parameter
  const { getAccessTokenSilently } = useAuth0();
  const toast = useToast();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  // Function to send invitations after league creation
  const sendInvitations = async (leagueId, emails, token) => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const invitePromises = emails.map((email) =>
      axios
        .post(
          `${apiUrl}/api/league/league-invite`,
          { leagueId, userEmail: email },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        )
        .catch((error) => {
          const errorMessage = error?.response?.data?.message || error.message;
          return { error: true, email, errorMessage };
        }),
    );

    try {
      const results = await Promise.allSettled(invitePromises);
      const successful = results.filter(
        (result) => result.status === "fulfilled" && !result.value?.error,
      ).length;

      // Analyze failures for user-friendly messaging
      const failures = [];
      results.forEach((result, index) => {
        if (result.status === "fulfilled" && result.value?.error) {
          const email = emails[index];
          const errorMessage = result.value.errorMessage;
          failures.push({ email, errorMessage });
        }
      });

      const userNotFoundCount = failures.filter(
        (f) =>
          f.errorMessage.includes("User not found") ||
          f.errorMessage.includes("not found"),
      ).length;

      return { successful, userNotFoundCount, totalFailed: failures.length };
    } catch (error) {
      console.error("Error sending invitations:", error);
      return {
        successful: 0,
        userNotFoundCount: 0,
        totalFailed: emails.length,
      };
    }
  };

  return useMutation(
    async ({
      leagueName,
      numPlayers,
      startDate,
      endDate,
      userId,
      autoSelectPenalty,
      invitedMembers,
      enableLeagueChat,
      eventIds, // NEW: Array of event IDs
    }) => {
      const token = await getAccessTokenSilently();
      const apiUrl = process.env.REACT_APP_API_URL;

      // First create the league
      const response = await axios.post(
        `${apiUrl}/api/league/create-league`,
        {
          leagueName,
          numPlayers,
          startDate,
          endDate,
          userId,
          autoSelectPenalty,
          enableLeagueChat,
          eventIds, // NEW: Send event IDs to backend
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      // Get league ID from response
      const leagueId = response.data?.leagueId || response.data?._id;

      // If we have a league ID and invitedMembers, send invitations
      let invitationResults = {
        successful: 0,
        userNotFoundCount: 0,
        totalFailed: 0,
      };
      const totalInvites = invitedMembers?.length || 0;
      if (leagueId && invitedMembers && invitedMembers.length > 0) {
        invitationResults = await sendInvitations(
          leagueId,
          invitedMembers,
          token,
        );
      }

      // Return combined response with invitation results
      return {
        ...response.data,
        invitationsSent: invitationResults.successful,
        totalInvites,
        userNotFoundCount: invitationResults.userNotFoundCount,
        totalFailed: invitationResults.totalFailed,
      };
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries("leagues");
        queryClient.invalidateQueries("leagueDetails");
        queryClient.invalidateQueries("leagueMembers");

        // Get invitation results
        const inviteCount = data?.invitationsSent || 0;
        const totalInvites = data?.totalInvites || 0;
        const userNotFoundCount = data?.userNotFoundCount || 0;
        // const totalFailed = data?.totalFailed || 0;

        // User-friendly toast messages
        if (totalInvites > 0) {
          if (inviteCount === totalInvites) {
            toast.success(
              `League created successfully! ${inviteCount} invitation${
                inviteCount === 1 ? "" : "s"
              } sent.`,
            );
          } else if (inviteCount > 0) {
            let message = `League created successfully! ${inviteCount} of ${totalInvites} invitations sent.`;
            if (userNotFoundCount > 0) {
              message += ` ${userNotFoundCount} user${
                userNotFoundCount === 1 ? "" : "s"
              } need to sign up first.`;
            }
            toast.warning(message);
          } else {
            let message =
              "League created successfully, but no invitations were sent.";
            if (userNotFoundCount > 0) {
              message += ` ${userNotFoundCount} user${
                userNotFoundCount === 1 ? "" : "s"
              } need to sign up first.`;
            }
            toast.error(message);
          }
        } else {
          toast.success("League created successfully!");
        }

        // Get the league ID from the response
        const leagueId = data?.leagueId || data?._id;

        if (leagueId) {
          // Set the league ID in Redux
          dispatch(setLeagueId(leagueId));

          // Navigate to the league details page
          navigate(`/league/${leagueId}`);
        } else {
          navigate("/leagues");
        }
      },
      onError: (error) => {
        console.error("Error creating league:", error);
        toast.error("Failed to create league. Please try again.");
      },
    },
  );
};
